<template>
  <div class="perf-page">
    <!-- 顶部综合评分大屏 -->
    <div class="score-board">
      <div class="score-main">
        <div class="score-ring">
          <div class="ring-inner">
            <div class="score-num">87.6</div>
            <div class="score-label">综合评分</div>
          </div>
          <svg class="ring-svg" viewBox="0 0 120 120">
            <circle class="ring-bg" cx="60" cy="60" r="54"/>
            <circle class="ring-fill" cx="60" cy="60" r="54" :style="{ strokeDashoffset: 340 - (340 * 0.876) }"/>
          </svg>
        </div>
        <div class="score-trend">
          <span class="trend-up">↑ 4.2%</span>
          <span class="trend-label">较上月</span>
        </div>
      </div>
      <div class="score-divider"></div>
      <div class="score-dims">
        <div v-for="(dim, idx) in dimensions" :key="idx" class="dim-item">
          <div class="dim-top">
            <span class="dim-name">{{ dim.name }}</span>
            <span class="dim-score" :style="{ color: dim.color }">{{ dim.score }}</span>
          </div>
          <div class="dim-bar">
            <div class="dim-fill" :style="{ width: dim.score + '%', background: dim.color }"></div>
          </div>
          <div class="dim-rank">行业排名: 前 {{ dim.rank }}%</div>
        </div>
      </div>
      <div class="score-divider"></div>
      <div class="score-rank">
        <div class="rank-title">本月评级分布</div>
        <div class="rank-pills">
          <div v-for="(r, idx) in rankDist" :key="idx" class="rank-pill" :style="{ borderColor: r.color }">
            <span class="pill-dot" :style="{ background: r.color }"></span>
            <span class="pill-label">{{ r.label }}</span>
            <span class="pill-num" :style="{ color: r.color }">{{ r.count }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 中间双栏：雷达图 + 仪表盘 -->
    <div class="chart-row">
      <div class="chart-box chart-left">
        <div class="chart-header">
          <span class="chart-title">◈ 供应商能力雷达对比</span>
          <div class="legend-group">
            <span class="legend-item"><span class="legend-dot" style="background:#00d4ff"></span>战略供应商均值</span>
            <span class="legend-item"><span class="legend-dot" style="background:#f0a030"></span>行业基准</span>
          </div>
        </div>
        <div ref="chartRadar" class="chart-body"></div>
      </div>
      <div class="chart-box chart-right">
        <div class="chart-header">
          <span class="chart-title">◉ 关键指标仪表盘</span>
        </div>
        <div class="gauge-wrap">
          <div v-for="(g, idx) in gauges" :key="idx" class="gauge-item">
            <div ref="gaugeRefs" class="gauge-chart"></div>
            <div class="gauge-label">{{ g.name }}</div>
            <div class="gauge-value" :style="{ color: g.color }">{{ g.value }}%</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部：绩效评估明细表格 -->
    <div class="chart-box table-box">
      <div class="chart-header">
        <span class="chart-title">◊ 供应商绩效评估明细</span>
        <div class="table-actions">
          <select v-model="filterQuarter" class="filter-select">
            <option value="Q2">2026 Q2</option>
            <option value="Q1">2026 Q1</option>
            <option value="Q4">2025 Q4</option>
          </select>
          <button class="action-btn" @click="handleExport">导出报告</button>
        </div>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>排名</th>
              <th>供应商</th>
              <th>质量得分</th>
              <th>交付得分</th>
              <th>价格得分</th>
              <th>服务得分</th>
              <th>创新得分</th>
              <th>综合得分</th>
              <th>评级</th>
              <th>趋势</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in perfList" :key="idx">
              <td>
                <span class="rank-badge" :class="{ top: idx < 3 }">{{ idx + 1 }}</span>
              </td>
              <td class="name-cell">
                <div class="supplier-name">{{ item.name }}</div>
                <div class="supplier-code">{{ item.code }}</div>
              </td>
              <td>
                <div class="mini-bar">
                  <div class="mini-fill" :style="{ width: item.quality + '%' }"></div>
                </div>
                <span class="mini-text">{{ item.quality }}</span>
              </td>
              <td>
                <div class="mini-bar">
                  <div class="mini-fill" :style="{ width: item.delivery + '%' }"></div>
                </div>
                <span class="mini-text">{{ item.delivery }}</span>
              </td>
              <td>
                <div class="mini-bar">
                  <div class="mini-fill" :style="{ width: item.price + '%' }"></div>
                </div>
                <span class="mini-text">{{ item.price }}</span>
              </td>
              <td>
                <div class="mini-bar">
                  <div class="mini-fill" :style="{ width: item.service + '%' }"></div>
                </div>
                <span class="mini-text">{{ item.service }}</span>
              </td>
              <td>
                <div class="mini-bar">
                  <div class="mini-fill" :style="{ width: item.innovation + '%' }"></div>
                </div>
                <span class="mini-text">{{ item.innovation }}</span>
              </td>
              <td>
                <span class="total-score" :style="{ color: scoreColor(item.total) }">{{ item.total }}</span>
              </td>
              <td>
                <span class="grade-badge" :class="item.grade">{{ item.grade }}</span>
              </td>
              <td>
                <span class="trend-arrow" :class="{ up: item.trend > 0, down: item.trend < 0 }">
                  {{ item.trend > 0 ? '↑' : '↓' }} {{ Math.abs(item.trend) }}
                </span>
              </td>
              <td>
                <button class="op-btn primary" @click="handleDetail(item)">详情</button>
                <button class="op-btn" @click="handleCompare(item)">对比</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <span class="footer-info">共 {{ perfList.length }} 条记录</span>
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">←</button>
          <span class="page-num" v-for="p in pageList" :key="p" :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</span>
          <button class="page-btn" :disabled="currentPage === totalPage" @click="currentPage++">→</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SrmPerformance',
  data() {
    return {
      dimensions: [
        { name: '质量合格率', score: 94.2, rank: 15, color: '#00d4ff' },
        { name: '准时交付率', score: 89.5, rank: 22, color: '#00e5a0' },
        { name: '价格竞争力', score: 82.3, rank: 35, color: '#f0a030' },
        { name: '服务响应度', score: 91.8, rank: 18, color: '#ff6b9d' },
        { name: '技术创新力', score: 78.6, rank: 42, color: '#a855f7' }
      ],
      rankDist: [
        { label: '战略级', count: 86, color: '#00d4ff' },
        { label: '核心级', count: 142, color: '#00e5a0' },
        { label: '优选级', count: 328, color: '#f0a030' },
        { label: '合格级', count: 512, color: '#8c9bb3' },
        { label: '观察级', count: 180, color: '#ff6b6b' }
      ],
      gauges: [
        { name: '质量合格率', value: 94.2, color: '#00d4ff' },
        { name: '准时交付率', value: 89.5, color: '#00e5a0' },
        { name: '成本节约率', value: 76.8, color: '#f0a030' },
        { name: '投诉处理率', value: 98.2, color: '#ff6b9d' }
      ],
      perfList: [
        { name: '深圳华强电子', code: 'SUP-001', quality: 98, delivery: 96, price: 88, service: 95, innovation: 92, total: 93.8, grade: '战略级', trend: 2.5 },
        { name: '苏州精密制造', code: 'SUP-002', quality: 97, delivery: 94, price: 85, service: 93, innovation: 88, total: 91.4, grade: '战略级', trend: 1.8 },
        { name: '杭州半导体', code: 'SUP-003', quality: 96, delivery: 92, price: 82, service: 90, innovation: 95, total: 91.0, grade: '核心级', trend: 3.2 },
        { name: '青岛海润化工', code: 'SUP-004', quality: 93, delivery: 89, price: 90, service: 88, innovation: 78, total: 87.6, grade: '核心级', trend: -0.5 },
        { name: '北京自动化设备', code: 'SUP-005', quality: 91, delivery: 87, price: 84, service: 92, innovation: 85, total: 87.8, grade: '核心级', trend: 1.2 },
        { name: '东莞金属制品', code: 'SUP-006', quality: 89, delivery: 91, price: 88, service: 85, innovation: 72, total: 85.0, grade: '优选级', trend: -1.5 },
        { name: '武汉光谷光电', code: 'SUP-007', quality: 88, delivery: 85, price: 86, service: 87, innovation: 90, total: 87.2, grade: '核心级', trend: 4.0 },
        { name: '成都西部智能', code: 'SUP-008', quality: 85, delivery: 82, price: 80, service: 84, innovation: 88, total: 83.8, grade: '优选级', trend: 0.8 }
      ],
      filterQuarter: 'Q2',
      currentPage: 1,
      totalPage: 3,
      charts: {}
    }
  },
  computed: {
    pageList() {
      return [1, 2, 3]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initRadarChart()
      this.initGauges()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(chart => chart && chart.dispose())
  },
  methods: {
    handleResize() {
      Object.values(this.charts).forEach(chart => chart && chart.resize())
    },
    scoreColor(score) {
      if (score >= 90) return '#00d4ff'
      if (score >= 85) return '#00e5a0'
      if (score >= 80) return '#f0a030'
      return '#ff6b6b'
    },
    handleDetail(item) {
      alert(`供应商评估详情：${item.name}\n\n质量：${item.quality}  交付：${item.delivery}\n价格：${item.price}  服务：${item.service}\n创新：${item.innovation}\n综合：${item.total}  评级：${item.grade}`)
    },
    handleCompare(item) {
      alert(`发起对比分析：${item.name}`)
    },
    handleExport() {
      alert('正在导出绩效评估报告...')
    },

    // 雷达图
    initRadarChart() {
      const chart = this.$echarts.init(this.$refs.chartRadar)
      this.charts.radar = chart
      chart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
          backgroundColor: 'rgba(1, 16, 57, 0.9)',
          borderColor: 'rgba(0, 212, 255, 0.3)',
          textStyle: { color: '#fff' }
        },
        radar: {
          indicator: [
            { name: '质量合格率', max: 100 },
            { name: '准时交付率', max: 100 },
            { name: '价格竞争力', max: 100 },
            { name: '服务响应度', max: 100 },
            { name: '技术创新力', max: 100 },
            { name: '合作稳定性', max: 100 }
          ],
          center: ['50%', '55%'],
          radius: '65%',
          axisName: { color: '#8c9bb3', fontSize: 12 },
          splitArea: {
            areaStyle: {
              color: ['rgba(0, 212, 255, 0.02)', 'rgba(0, 212, 255, 0.05)', 'rgba(0, 212, 255, 0.02)', 'rgba(0, 212, 255, 0.05)']
            }
          },
          axisLine: { lineStyle: { color: 'rgba(0, 212, 255, 0.15)' } },
          splitLine: { lineStyle: { color: 'rgba(0, 212, 255, 0.1)' } }
        },
        series: [{
          type: 'radar',
          data: [
            {
              value: [94, 90, 82, 92, 79, 88],
              name: '战略供应商均值',
              symbol: 'none',
              lineStyle: { color: '#00d4ff', width: 2 },
              areaStyle: { color: 'rgba(0, 212, 255, 0.15)' }
            },
            {
              value: [85, 82, 78, 80, 72, 75],
              name: '行业基准',
              symbol: 'none',
              lineStyle: { color: '#f0a030', width: 2, type: 'dashed' },
              areaStyle: { color: 'rgba(240, 160, 48, 0.08)' }
            }
          ]
        }]
      })
    },

    // 仪表盘
    initGauges() {
      this.gauges.forEach((g, idx) => {
        const el = this.$refs.gaugeRefs[idx]
        if (!el) return
        const chart = this.$echarts.init(el)
        this.charts[`gauge${idx}`] = chart
        chart.setOption({
          backgroundColor: 'transparent',
          series: [{
            type: 'gauge',
            radius: '90%',
            center: ['50%', '55%'],
            startAngle: 200,
            endAngle: -20,
            min: 0,
            max: 100,
            splitNumber: 5,
            axisLine: {
              lineStyle: {
                width: 8,
                color: [[g.value / 100, g.color], [1, 'rgba(140, 155, 179, 0.1)']]
              }
            },
            pointer: {
              itemStyle: { color: g.color },
              width: 4,
              length: '60%'
            },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            detail: { show: false },
            data: [{ value: g.value }]
          }]
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.perf-page {
  width: 100%;
  min-height: 100%;
  padding-bottom: 20px;
}

/* ==================== 综合评分大屏 ==================== */
.score-board {
  display: flex;
  align-items: center;
  gap: 24px;
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 16px;
}

.score-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.score-ring {
  position: relative;
  width: 140px;
  height: 140px;
}

.ring-inner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.score-num {
  font-size: 36px;
  font-weight: bold;
  color: #00d4ff;
  line-height: 1;
}

.score-label {
  font-size: 12px;
  color: #8c9bb3;
  margin-top: 4px;
}

.ring-svg {
  width: 140px;
  height: 140px;
  transform: rotate(-90deg);

  .ring-bg {
    fill: none;
    stroke: rgba(140, 155, 179, 0.1);
    stroke-width: 8;
  }

  .ring-fill {
    fill: none;
    stroke: #00d4ff;
    stroke-width: 8;
    stroke-linecap: round;
    stroke-dasharray: 340;
    transition: stroke-dashoffset 1s ease;
  }
}

.score-trend {
  display: flex;
  align-items: center;
  gap: 6px;

  .trend-up {
    color: #00e5a0;
    font-size: 14px;
    font-weight: bold;
  }

  .trend-label {
    color: #8c9bb3;
    font-size: 12px;
  }
}

.score-divider {
  width: 1px;
  height: 120px;
  background: rgba(0, 212, 255, 0.1);
}

.score-dims {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dim-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dim-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dim-name {
  font-size: 13px;
  color: #c8d4e8;
}

.dim-score {
  font-size: 16px;
  font-weight: bold;
}

.dim-bar {
  height: 6px;
  background: rgba(140, 155, 179, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.dim-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.8s ease;
}

.dim-rank {
  font-size: 11px;
  color: #8c9bb3;
}

.score-rank {
  width: 200px;
  flex-shrink: 0;
}

.rank-title {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  margin-bottom: 14px;
}

.rank-pills {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rank-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid;
  background: rgba(0, 212, 255, 0.03);
}

.pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.pill-label {
  font-size: 12px;
  color: #c8d4e8;
  flex: 1;
}

.pill-num {
  font-size: 14px;
  font-weight: bold;
}

/* ==================== 中间双栏 ==================== */
.chart-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.chart-box {
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.chart-title {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  letter-spacing: 1px;
}

.legend-group {
  display: flex;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #8c9bb3;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.chart-body {
  flex: 1;
  min-height: 300px;
}

/* ==================== 仪表盘区域 ==================== */
.gauge-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  flex: 1;
}

.gauge-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gauge-chart {
  width: 100%;
  height: 120px;
}

.gauge-label {
  font-size: 12px;
  color: #8c9bb3;
  margin-top: 4px;
}

.gauge-value {
  font-size: 18px;
  font-weight: bold;
  margin-top: 2px;
}

/* ==================== 表格区域 ==================== */
.table-box {
  min-height: 400px;
}

.table-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.filter-select {
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 212, 255, 0.05);
  color: #8c9bb3;
  font-size: 12px;
  outline: none;
  cursor: pointer;

  option {
    background: #011039;
    color: #8c9bb3;
  }
}

.action-btn {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 212, 255, 0.08);
  color: #00d4ff;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 212, 255, 0.15);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
}

.table-wrap {
  flex: 1;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.05);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.2);
    border-radius: 2px;
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 12px 14px;
    text-align: left;
    border-bottom: 1px solid rgba(0, 212, 255, 0.08);
    white-space: nowrap;
  }

  th {
    background: rgba(0, 212, 255, 0.06);
    color: #8c9bb3;
    font-size: 12px;
    font-weight: normal;
    letter-spacing: 1px;
  }

  tbody tr {
    transition: all 0.2s;

    &:hover {
      background: rgba(0, 212, 255, 0.04);
    }
  }
}

.rank-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #8c9bb3;
  background: rgba(140, 155, 179, 0.1);

  &.top {
    background: rgba(0, 212, 255, 0.15);
    color: #00d4ff;
    font-weight: bold;
  }
}

.name-cell {
  .supplier-name {
    color: #fff;
    font-size: 13px;
  }

  .supplier-code {
    color: #8c9bb3;
    font-size: 11px;
    margin-top: 2px;
  }
}

.mini-bar {
  width: 60px;
  height: 4px;
  background: rgba(140, 155, 179, 0.15);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}

.mini-fill {
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #00e5a0);
  border-radius: 2px;
  transition: width 0.5s ease;
}

.mini-text {
  font-size: 11px;
  color: #8c9bb3;
}

.total-score {
  font-size: 16px;
  font-weight: bold;
}

.grade-badge {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;

  &.战略级 {
    background: rgba(0, 212, 255, 0.1);
    color: #00d4ff;
  }

  &.核心级 {
    background: rgba(0, 229, 160, 0.1);
    color: #00e5a0;
  }

  &.优选级 {
    background: rgba(240, 160, 48, 0.1);
    color: #f0a030;
  }

  &.合格级 {
    background: rgba(140, 155, 179, 0.1);
    color: #8c9bb3;
  }

  &.观察级 {
    background: rgba(255, 107, 107, 0.1);
    color: #ff6b6b;
  }
}

.trend-arrow {
  font-size: 12px;

  &.up {
    color: #00e5a0;
  }

  &.down {
    color: #ff6b6b;
  }
}

.op-btn {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  border: none;
  margin-right: 6px;
  transition: all 0.2s;

  &.primary {
    background: rgba(0, 212, 255, 0.15);
    color: #00d4ff;

    &:hover {
      background: rgba(0, 212, 255, 0.3);
    }
  }

  &:not(.primary) {
    background: rgba(140, 155, 179, 0.1);
    color: #8c9bb3;

    &:hover {
      background: rgba(0, 212, 255, 0.1);
      color: #00d4ff;
    }
  }
}

/* ==================== 分页 ==================== */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 212, 255, 0.08);
  margin-top: 8px;
}

.footer-info {
  font-size: 12px;
  color: #8c9bb3;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: transparent;
  color: #8c9bb3;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #00d4ff;
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.08);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.page-num {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #8c9bb3;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(0, 212, 255, 0.3);
    color: #00d4ff;
  }

  &.active {
    background: rgba(0, 212, 255, 0.15);
    border-color: #00d4ff;
    color: #00d4ff;
  }
}

/* ==================== 响应式 ==================== */
@media (max-width: 1400px) {
  .score-board {
    flex-direction: column;
    gap: 20px;
  }

  .score-divider {
    width: 100%;
    height: 1px;
  }

  .score-rank {
    width: 100%;
  }

  .chart-row {
    grid-template-columns: 1fr;
  }

  .gauge-wrap {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .gauge-wrap {
    grid-template-columns: repeat(2, 1fr);
  }

  .rank-pills {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .op-btn {
    padding: 2px 6px;
    font-size: 10px;
    margin-right: 3px;
  }
}
</style>